/**
 * Bosun users — `auth.get_users` / `auth.create_user` / `auth.update_user`.
 *
 * Backs `/settings/users` (spec: docs/superpowers/specs/2026-08-09-users-in-settings-design.md).
 * Read that spec's §3 before changing anything here: three of the decisions below look wrong
 * without it, and all three are working around measured server behaviour.
 *
 * 1. **`enabled_only` is never sent.** The server ignores it — a disabled user is absent from
 *    every list shape, filtered or not. Sending a parameter the server discards would tell the
 *    next reader it works. Its absence, plus this comment, is the honest encoding.
 * 2. **The generated password never leaves this module.** `auth.create_user` demands one, Clerk
 *    is the sign-in path, so it is vestigial — generated here, sent once, and deliberately NOT
 *    returned, so no component can render, log or accidentally report it.
 * 3. **`role` is sent in BOSUN's vocabulary** (`purchaser`), not Frappe's (`Bosun Purchaser`).
 *    The server maps it. Frappe's roles are authoritative and the resolver accepts ERPNext's
 *    stock roles too, which is why `toUser` may receive a `role` the `?role=` filter would never
 *    have matched.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { callGet, callPost, callPut } from './bosun'
import type { WireList } from './wire'
import { qk } from './keys'
import { generateInitialPassword } from '../initialPassword'
import { ROLES, type Role } from '../types'

export interface WireUser {
  email?: unknown
  full_name?: unknown
  enabled?: unknown
  role?: unknown
  frappe_roles?: unknown
}

export interface BosunUser {
  /** The Frappe `User` docname — an email. Identity; never a display label. */
  email: string
  fullName: string
  /** `null` when the server sent something outside our three. Never coerced: an unexpected
   *  value must not silently grant rights. */
  role: Role | null
  enabled: boolean
  /** Shown when `role` is null — the raw roles are what tells a manager what to change in the ERP. */
  frappeRoles: string[]
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

export function toUser(w: WireUser): BosunUser {
  const role = typeof w.role === 'string' && (ROLES as readonly string[]).includes(w.role)
    ? (w.role as Role)
    : null
  return {
    email: str(w.email),
    fullName: str(w.full_name),
    role,
    enabled: Number(w.enabled) === 1,
    frappeRoles: Array.isArray(w.frappe_roles) ? w.frappe_roles.filter((r): r is string => typeof r === 'string') : [],
  }
}

/** `Guest` is a Frappe system account, not a person, and it comes back in the live list.
 *  Dropped here rather than hidden in the component, so it never reaches the UI at all. */
const SYSTEM_ACCOUNTS = new Set(['Guest', 'Administrator'])

// `role` is unused by every caller today — do NOT pass it until the `?role=` ask (A6b) lands.
// The server's `?role=` filter matches only the `Bosun *` roles, while the `role` field it
// returns also accepts ERPNext's stock equivalents (spec §3.1/§3.3), so a role-filtered call
// here would silently omit real purchasers/managers provisioned through a stock role — exactly
// the mismatch this file's header and the spec exist to avoid.
export function useUsers(role?: Role, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qk.users(role),
    queryFn: async (): Promise<BosunUser[]> => {
      // No `enabled_only` — see this file's header, point 1.
      const page = await callGet<WireList<WireUser>>('auth', 'get_users', { role })
      return page.data.map(toUser).filter((u) => !SYSTEM_ACCOUNTS.has(u.email))
    },
    enabled: options?.enabled,
  })
}

export interface CreateUserInput {
  email: string
  fullName: string
  role: Role
}

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateUserInput): Promise<BosunUser> => {
      const w = await callPost<WireUser>('auth', 'create_user', {
        email: input.email,
        full_name: input.fullName,
        role: input.role,
        // Generated, sent, and forgotten — see this file's header, point 2.
        password: generateInitialPassword(),
      })
      return toUser(w)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export interface UpdateUserInput {
  email: string
  role?: Role
  enabled?: boolean
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateUserInput): Promise<BosunUser> => {
      const w = await callPut<WireUser>('auth', 'update_user', {
        user: input.email,
        ...(input.role !== undefined ? { role: input.role } : {}),
        ...(input.enabled !== undefined ? { enabled: input.enabled ? 1 : 0 } : {}),
      })
      return toUser(w)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] })
    },
  })
}
